import { cn } from "../../ui/utils";
import { HIDDEN_SWATCH_COLOR } from "../../charts/donutCore";
import { formatValue } from "./chartTheme";
import type { NormalizedSeries } from "./resolveChart";

interface TooltipPayloadItem {
  name?: string | number;
  value?: number | string;
  color?: string;
  dataKey?: string | number;
  payload?: Record<string, unknown> & { fill?: string };
}
interface InjectedTooltip {
  active?: boolean;
  label?: string | number;
  payload?: TooltipPayloadItem[];
}

const tooltipShell =
  "pointer-events-none max-w-[15rem] rounded-lg border border-border bg-popover px-2.5 py-2 text-[0.6875rem] shadow-xl";

export function CartesianTooltip({
  active,
  label,
  payload,
  unit = "",
  singleSeries = false,
  total = 0,
}: InjectedTooltip & { unit?: string; singleSeries?: boolean; total?: number }) {
  if (!active || !payload?.length) return null;
  return (
    <div className={tooltipShell}>
      {label != null && String(label) !== "" && (
        <p className="mb-1.5 font-semibold text-foreground">{String(label)}</p>
      )}
      <div className="space-y-1">
        {payload.map((p, i) => {
          const color = p.color ?? p.payload?.fill;
          const value = Number(p.value);
          const share = singleSeries && total > 0 ? Math.round((value / total) * 100) : null;
          return (
            <div key={i} className="flex items-center gap-2">
              <span className="size-2 shrink-0 rounded-[3px]" style={{ background: color }} />
              {!singleSeries && (
                <span className="min-w-0 flex-1 truncate text-muted-foreground">{p.name}</span>
              )}
              <span className={cn("font-semibold tabular-nums text-foreground", !singleSeries && "ml-auto")}>
                {formatValue(value, unit)}
                {share != null && (
                  <span className="ml-1 font-normal text-muted-foreground">({share}%)</span>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function RadialTooltip({
  active,
  payload,
  unit = "",
  total = 0,
}: InjectedTooltip & { unit?: string; total?: number }) {
  if (!active || !payload?.length) return null;
  const item = payload[0];
  const value = Number(item.value);
  const color = item.payload?.fill ?? item.color;
  const share = total > 0 ? Math.round((value / total) * 100) : null;
  return (
    <div className={tooltipShell}>
      <div className="flex items-center gap-2">
        <span className="size-2 shrink-0 rounded-[3px]" style={{ background: color }} />
        <span className="min-w-0 flex-1 truncate font-semibold text-foreground">{String(item.name ?? "")}</span>
        <span className="ml-auto font-semibold tabular-nums text-foreground">
          {formatValue(value, unit)}
          {share != null && <span className="ml-1 font-normal text-muted-foreground">({share}%)</span>}
        </span>
      </div>
    </div>
  );
}

export function CartesianLegend({ series }: { series: NormalizedSeries[] }) {
  return (
    <ul className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1">
      {series.map((s) => (
        <li key={s.key} className="flex items-center gap-1.5 text-[0.6875rem] font-medium text-muted-foreground">
          <span className="size-2 shrink-0 rounded-[3px]" style={{ background: s.color }} />
          {s.label}
        </li>
      ))}
    </ul>
  );
}

export interface RadialLegendItem {
  label: string;
  value: number;
  color: string;
  share: number;
}

export function RadialLegend({
  items,
  unit = "",
  active,
  hidden,
  onToggle,
}: {
  items: RadialLegendItem[];
  unit?: string;
  /**
   * Index of the slice hovered on the donut, so its row highlights. One-way on purpose: hovering a
   * row does NOT drive the chart back, because the pop is barely visible at this size and all the
   * user saw was the centre count changing on its own. The dashboard card's legend is click-only too.
   */
  active: number | null;
  /** Labels currently toggled off. Omit to render a non-interactive legend. */
  hidden?: ReadonlySet<string>;
  /** Click a row to hide/show its slice, as the dashboard card does. Omit to disable toggling. */
  onToggle?: (label: string) => void;
}) {
  return (
    <ul className="min-w-0 flex-1 space-y-0.5 self-stretch">
      {items.map((it, i) => {
        const on = active === i;
        const off = hidden?.has(it.label) ?? false;
        return (
          <li key={i}>
            {/* A button, not the li itself: toggling has to be reachable by keyboard too. */}
            <button
              type="button"
              onClick={onToggle ? () => onToggle(it.label) : undefined}
              title={onToggle ? (off ? `Show ${it.label}` : `Hide ${it.label}`) : undefined}
              aria-pressed={onToggle ? !off : undefined}
              className={cn(
                "flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-left text-xs leading-snug transition-colors",
                onToggle ? "cursor-pointer" : "cursor-default",
                on && "bg-foreground/[0.06]",
                off && "opacity-40",
              )}
            >
              <span
                className="size-2.5 shrink-0 rounded-sm"
                style={{ background: off ? HIDDEN_SWATCH_COLOR : it.color }}
              />
              <span className={cn("min-w-0 flex-1 break-words text-foreground", on && "font-semibold")}>
                {it.label}
              </span>
              {/* No share column: the percentage is drawn on the chart itself, next to its slice,
                  the way the dashboard card does it. Crammed in here it wrapped ("100" / "%") and
                  pushed that row out of line with the others. */}
              <span className="shrink-0 whitespace-nowrap font-semibold tabular-nums text-foreground">
                {formatValue(it.value, unit)}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
